import { useState } from "react";
import { echotwinApi } from "@/lib/echotwin-api";
import type { PublicCaseCard } from "@/hooks/useCaseSearch";

/**
 * EchoTwin 전체 검색(searchCases) 결과 카드 — "전문가 PICK" 카드와 필드가 다르다.
 * 사건번호·법원·유찰횟수·강스터디 등급은 이 API가 안 줘서 없는 채로 둔다(지어내지 않음).
 */
const PROPERTY_TYPE_LABEL: Record<string, string> = {
  apartment: "아파트",
  multi_family: "다세대",
  multi_unit: "다가구",
  row_house: "연립",
  detached: "단독",
  officetel: "오피스텔",
  store_house: "근린주택",
  urban_life: "도시형생활",
};

function eok(v: number): string {
  return `${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}억`;
}

export default function LiveCaseCard({ c }: { c: PublicCaseCard }) {
  const [imgError, setImgError] = useState(false);
  const showPhoto = c.hasPhoto && !imgError;

  return (
    <div className="bg-white rounded-xl border border-[#E8E5E0] overflow-hidden hover:border-[#C4956A]/30 transition-all">
      <div className="relative h-44 overflow-hidden bg-[#FAFAF8]">
        {showPhoto ? (
          <img
            loading="lazy"
            decoding="async"
            src={echotwinApi.casePhotoUrl(c.id, 0)}
            alt={`${PROPERTY_TYPE_LABEL[c.propertyType] ?? c.propertyType} - ${c.area}`}
            className="w-full h-full object-cover object-top"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <i className="ri-building-2-line text-3xl text-[#D1CCC2]"></i>
          </div>
        )}
        <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5">
          <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-white/90 text-[#1C1917]">
            {PROPERTY_TYPE_LABEL[c.propertyType] ?? c.propertyType}
          </span>
          {c.dday <= 7 && (
            <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-rose-500 text-white">
              D-{c.dday}
            </span>
          )}
        </div>
      </div>

      <div className="p-3.5">
        <p className="text-sm font-medium text-[#1C1917] truncate">{c.area}</p>
        <p className="text-xs text-[#8C8780] mt-0.5">
          할인율 {c.discountPct}% · 입찰일 {c.auctionDate}
        </p>

        <div className="mt-2 space-y-1 text-xs">
          <div className="flex justify-between">
            <span className="text-[#8C8780]">감정가</span>
            <span className="text-[#5C5650]">{eok(c.appraisedEok)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#8C8780]">최저가</span>
            <span className="text-[#C4956A] font-bold">{eok(c.minBidEok)}</span>
          </div>
        </div>

        <div className="mt-2 pt-2 border-t border-[#F0EDE8] flex items-center justify-between">
          {c.riskCount > 0 && (
            <span className="text-[11px] text-rose-600">
              <i className="ri-error-warning-line mr-0.5"></i>위험 {c.riskCount}건
            </span>
          )}
          {c.marginPct != null && !c.priceWarn ? (
            <span className="ml-auto text-xs font-bold text-emerald-600">예상차익 {c.marginPct}%</span>
          ) : c.priceWarn ? (
            <span className="ml-auto text-[11px] text-[#8C8780]">차익 추정 불가(지분·통합감정 등)</span>
          ) : (
            <span className="ml-auto text-[11px] text-[#8C8780]">시세 분석 준비중</span>
          )}
        </div>
      </div>
    </div>
  );
}
